import { ReactNode, use, useEffect } from 'react';
import { bundleScriptPattern } from '../../../common/src/paths.ts';
import { LocalStorageKey, localStorageRemove, localStorageSet } from '../data/client_local_storage.ts';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { useCustomMusic } from '../data/use_custom_music.tsx';
import { HymnalsContext } from '../data/use_hymnals.tsx';
import {
  refreshMusicCache,
  setMusicCacheCustomMusic,
  setMusicCacheCustomMusicLoadedOrgIds,
  setMusicCacheDownloadFunctions,
  setMusicCacheHymnals,
  setMusicCacheOnline,
  setMusicCacheOrgId,
} from './music_cache_refresh.ts';
import { useDownloads } from './use_downloads.tsx';
import { useOnline } from './use_online.tsx';

// A page that wants a music update check on mount (issue #3517) - the check itself is app-wide
// and also runs on app start, on returning to the tab and when the network comes back.
export function useMusicCache() {
  useEffect(() => {
    void refreshMusicCache({ trigger: 'mount' });
  }, []);
}

// Pushes the refresher's inputs into the module-level store, so refreshMusicCache() can run
// without any component being mounted. Exposes no context value - nothing needs one.
export const MusicCacheProvider = ({ children }: { children: ReactNode }) => {
  const isOnline = useOnline();
  const { currentOrg } = useCurrentOrg();
  const hymnals = use(HymnalsContext);
  const { customMusic, customMusicLoadedOrgIds } = useCustomMusic();
  const { add: addDownload, downloadsComplete } = useDownloads();

  useEffect(() => {
    setMusicCacheOnline(isOnline);
  }, [isOnline]);

  useEffect(() => {
    setMusicCacheOrgId(currentOrg?.id);
  }, [currentOrg?.id]);

  useEffect(() => {
    setMusicCacheHymnals(hymnals);
  }, [hymnals]);

  useEffect(() => {
    setMusicCacheCustomMusic(customMusic);
  }, [customMusic]);

  useEffect(() => {
    setMusicCacheCustomMusicLoadedOrgIds(customMusicLoadedOrgIds);
  }, [customMusicLoadedOrgIds]);

  useEffect(() => {
    setMusicCacheDownloadFunctions({ addDownload, downloadsComplete });
  }, [addDownload, downloadsComplete]);

  return children;
};

export function recordBundleHash() {
  // a Vite dev server (Storybook, Vitest) has no bundle hash - only builds emit hashed bundles,
  // and since issue #3447 the app is always served from a build, local development included
  if (import.meta.env.DEV) {
    localStorageRemove(LocalStorageKey.BundleHash);
    return;
  }

  const scriptElements = Array.from(document.head.children).filter(
    (child) => child.tagName === 'SCRIPT',
  ) as HTMLScriptElement[];
  if (scriptElements.length === 0) {
    localStorageRemove(LocalStorageKey.BundleHash);
    return;
  }

  const bundleHashes = scriptElements
    .map((scriptElement) => scriptElement.src.match(bundleScriptPattern)?.groups?.bundleHash)
    .filter((bundleHash) => !!bundleHash);
  if (bundleHashes.length !== 1) {
    console.error(`Expected a single bundle hash on the document scripts, found ${bundleHashes.length}`);
    return;
  }
  localStorageSet(LocalStorageKey.BundleHash, bundleHashes[0]);
}
